import {
    updatePrioritizeRequestStatusMutator,
    PrioritizeRequestStatus,
    getUserStore,
} from 'owa-mail-priority-inbox-store';
import {
    ArrowCircleUpSparkleRegular,
    DismissCircle16Regular,
    Options16Regular,
} from '@fluentui/react-icons';
import loc, { formatToArray } from 'owa-localize';
import { getStore as getListViewStore, isPriorityView } from 'owa-mail-list-store';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { observer } from 'owa-mobx-react';
import React from 'react';
import {
    prioritizeNudgeTextTemplate,
    prioritizeNudgeTextBold,
    prioritizeNudgeTextDescription,
    prioritizeNudgeTextDescriptionOnDelivery,
    dontShowAgainMenuText,
    customizeInstructionsMenuText,
} from './PrioritizeNudge.locstring.json';
import { nudgedIcon, boldText } from './PrioritizeNudge.scss';
import type { MailFolderTableQuery } from 'owa-mail-list-store';
import { logPrioritizeTelemetryEvent } from 'owa-copilot-telemetry/lib/prioritizeTelemetryLogger';
import { lazyOpenPriorityView } from 'owa-mail-priority-inbox';
import {
    isPriorityNudgeEnabled,
    isPrioritizeOnDeliveryOn,
    isPrioritizeOnDemandOn,
} from 'owa-copilot-settings-utils';
import MailListNudge from './MailListNudge';
import type { EllipsisMenuItem } from './MailListNudge';
import type FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { getLogicalRing } from 'owa-config';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { getCopilotPrioritizeSettingsPath } from 'owa-options-copilot/lib/utils/getCopilotSettingsPath';

export interface PrioritizeNudgeProps {
    tableViewId: string;
}

const icon = <ArrowCircleUpSparkleRegular className={nudgedIcon} />;
const dontShowAgainIcon = <DismissCircle16Regular />;
const customizeInstructionsIcon = <Options16Regular />;

export default observer(function PrioritizeNudge(props: PrioritizeNudgeProps): JSX.Element | null {
    const tableView = getListViewStore().tableViews.get(props.tableViewId);
    const tableQuery = tableView?.tableQuery as MailFolderTableQuery;
    const mailboxInfo = getMailboxInfo(tableView);

    const isPrioritizeViewSelected = isPriorityView(tableQuery);

    const userStore = getUserStore(mailboxInfo);
    const { prioritizeRequestStatus } = userStore;

    const isNudged = prioritizeRequestStatus === PrioritizeRequestStatus.Nudged;
    const hasFeature = isPrioritizeOnDemandOn(mailboxInfo);

    const ring = getLogicalRing();
    const isInternalUser = ring === 'Dogfood' || ring === 'Microsoft';

    React.useEffect(() => {
        if (isNudged && !hasFeature && isInternalUser) {
            logPrioritizeTelemetryEvent('PrioritizeNudgeTrigger', mailboxInfo, {
                isPrioritizeOnDeliveryOn: isPrioritizeOnDeliveryOn(mailboxInfo),
            });

            // Update timestamp so trigger only fires once and matches the actual
            // conditions of the feature.
            Promise.resolve().then(() => {
                updatePrioritizeRequestStatusMutator(
                    mailboxInfo,
                    PrioritizeRequestStatus.None,
                    true /* shouldUpdatePrioritizeNudgeTimestamp */
                );
            });
        }
    }, [isNudged, hasFeature, isInternalUser, mailboxInfo]);

    const onRendered = React.useCallback(() => {
        logPrioritizeTelemetryEvent('PrioritizeNudgeRendered', mailboxInfo, {
            isPrioritizeOnDeliveryOn: isPrioritizeOnDeliveryOn(mailboxInfo),
        });
    }, [mailboxInfo]);

    const onClick = React.useCallback(() => {
        if (tableView) {
            lazyOpenPriorityView.importAndExecute(mailboxInfo, 'Nudge' /* source */);
        }
    }, [mailboxInfo, tableView]);

    const onDismiss = React.useCallback(
        (evt: React.MouseEvent<unknown> | React.KeyboardEvent<unknown>) => {
            evt.stopPropagation();
            logPrioritizeTelemetryEvent('PrioritizeNudgeDismissed', mailboxInfo);

            updatePrioritizeRequestStatusMutator(
                mailboxInfo,
                PrioritizeRequestStatus.None,
                true /* shouldUpdatePrioritizeNudgeTimestamp */
            );
        },
        [mailboxInfo]
    );

    const onDontShowAgain = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            evt.stopPropagation();
            logPrioritizeTelemetryEvent('PrioritizeNudgeDontShowAgain', mailboxInfo);

            updatePrioritizeRequestStatusMutator(
                mailboxInfo,
                PrioritizeRequestStatus.None,
                true /* shouldUpdatePrioritizeNudgeTimestamp */
            );

            const settingsPath = getCopilotPrioritizeSettingsPath();
            lazyMountAndShowFullOptions.importAndExecute(
                settingsPath.category,
                settingsPath.subcategory,
                undefined,
                undefined,
                mailboxInfo
            );
        },
        [mailboxInfo]
    );

    const onCustomizeInstructions = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            evt.stopPropagation();
            logPrioritizeTelemetryEvent('PrioritizeNudgeCustomizeInstructions', mailboxInfo);

            const settingsPath = getCopilotPrioritizeSettingsPath();
            lazyMountAndShowFullOptions.importAndExecute(
                settingsPath.category,
                settingsPath.subcategory,
                undefined,
                undefined,
                mailboxInfo
            );
        },
        [mailboxInfo]
    );

    const ellipsisMenuItems: EllipsisMenuItem[] = React.useMemo(
        () => [
            {
                key: 'dontShowAgain',
                icon: dontShowAgainIcon,
                text: loc(dontShowAgainMenuText),
                onClick: onDontShowAgain,
            },
            {
                key: 'customizeInstructions',
                icon: customizeInstructionsIcon,
                text: loc(customizeInstructionsMenuText),
                onClick: onCustomizeInstructions,
            },
        ],
        [onDontShowAgain, onCustomizeInstructions]
    );

    if (
        !isNudged ||
        isPrioritizeViewSelected ||
        !isPriorityNudgeEnabled(mailboxInfo) ||
        tableQuery?.focusedViewFilter === 1
    ) {
        return null;
    }

    const text = formatToArray(
        loc(prioritizeNudgeTextTemplate),
        <span className={boldText}>{loc(prioritizeNudgeTextBold)}</span>,
        isPrioritizeOnDeliveryOn(mailboxInfo)
            ? loc(prioritizeNudgeTextDescriptionOnDelivery)
            : loc(prioritizeNudgeTextDescription)
    );

    return (
        <MailListNudge
            icon={icon}
            text={text}
            onClick={onClick}
            onDismiss={onDismiss}
            onRendered={onRendered}
            ellipsisMenuItems={ellipsisMenuItems}
            tableView={tableView}
        />
    );
}, 'PrioritizeNudge') as React.FunctionComponent<PrioritizeNudgeProps>;
